(() => {
  'use strict';

  const SELECTOR = '[data-zpc7]';
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqFine = window.matchMedia('(pointer:fine)');

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const mod = (n, m) => ((n % m) + m) % m;
  const pad = (n) => String(n).padStart(2, '0');

  function initCarousel(root) {
    if (!root || root.dataset.zpc7Ready === 'true') return;
    const track = root.querySelector('[data-zpc7-track]');
    const originals = Array.from(track?.children || []).filter((el) => el.matches('[data-zpc7-item]'));
    if (!track || originals.length < 1) return;

    root.dataset.zpc7Ready = 'true';
    const count = originals.length;
    const prev = root.querySelector('[data-zpc7-prev]');
    const next = root.querySelector('[data-zpc7-next]');
    const dotsWrap = root.querySelector('[data-zpc7-dots]');
    const progress = root.querySelector('[data-zpc7-progress]');
    const currentEl = root.querySelector('[data-zpc7-current]');
    const totalEl = root.querySelector('[data-zpc7-total]');

    const autoplayEnabled = root.dataset.autoplay === 'true';
    const autoplayMobile = root.dataset.autoplayMobile !== 'false';
    const autoplayEditor = root.dataset.autoplayEditor !== 'false';
    const pauseHover = root.dataset.pauseHover === 'true';
    const pauseInteraction = root.dataset.pauseInteraction !== 'false';
    const dragEnabled = root.dataset.enableDrag === 'true';
    const speed = Math.max(1500, Number(root.dataset.speed) || 4000);
    const resumeDelay = Math.max(500, Number(root.dataset.resumeDelay) || 4000);
    const duration = Math.max(100, Number(root.dataset.transitionDuration) || 850);
    const easingName = root.dataset.transitionEasing || 'luxury';
    const designMode = Boolean(window.Shopify && Shopify.designMode);
    let direction = root.dataset.autoplayDirection === 'prev' ? -1 : 1;

    let logicalIndex = 0;
    let physicalIndex = count;
    let timer = null;
    let resumeTimer = null;
    let scrollRaf = null;
    let scrollAnim = null;
    let destroyed = false;
    let hovering = false;
    let focusWithin = false;
    let userPaused = false;
    let drag = null;
    let suppressClick = false;
    let resizeObserver = null;
    let scrollEndTimer = null;
    const cleanups = [];

    // V8.2 infinite buffer: one complete clone set before and after the real products.
    const cloneSet = (setName) => originals.map((item, idx) => {
      const clone = item.cloneNode(true);
      clone.dataset.zpc7Clone = setName;
      clone.removeAttribute('data-shopify-editor-block');
      [clone, ...clone.querySelectorAll('*')].forEach((el) => {
        [...el.attributes].forEach((attr) => {
          if (attr.name.startsWith('data-shopify-editor')) el.removeAttribute(attr.name);
        });
      });
      clone.querySelectorAll('[id]').forEach((el) => {
        const old = el.id;
        const nextId = `${old}--zpc7-${setName}-${idx}`;
        clone.querySelectorAll(`[for="${CSS.escape(old)}"]`).forEach((label) => label.setAttribute('for', nextId));
        el.id = nextId;
      });
      return clone;
    });

    let beforeClones = [];
    let afterClones = [];
    if (count > 1) {
      beforeClones = cloneSet('before');
      afterClones = cloneSet('after');
      const beforeFrag = document.createDocumentFragment();
      beforeClones.forEach((el) => beforeFrag.appendChild(el));
      track.insertBefore(beforeFrag, track.firstChild);
      const afterFrag = document.createDocumentFragment();
      afterClones.forEach((el) => afterFrag.appendChild(el));
      track.appendChild(afterFrag);
    }

    const allItems = () => Array.from(track.children).filter((el) => el.matches('[data-zpc7-item]'));
    const getGap = () => parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
    const getStep = () => (originals[0]?.getBoundingClientRect().width || track.clientWidth) + getGap();
    const leftForPhysical = (index) => index * getStep();

    const ease = (t) => {
      if (easingName === 'linear') return t;
      if (easingName === 'snappy') return 1 - Math.pow(1 - t, 4.8);
      if (easingName === 'smooth') return t * t * (3 - 2 * t);
      if (easingName === 'cinematic') return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      return 1 - Math.pow(1 - t, 3.35);
    };

    const stopScrollAnimation = () => {
      if (scrollAnim) cancelAnimationFrame(scrollAnim);
      scrollAnim = null;
      track.style.scrollSnapType = '';
    };

    const setScrollInstant = (left) => {
      stopScrollAnimation();
      const prior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = prior;
    };

    const animateTo = (left, done) => {
      stopScrollAnimation();
      if (mqReduce.matches || duration <= 100) {
        track.scrollLeft = left;
        done?.();
        return;
      }
      const start = track.scrollLeft;
      const delta = left - start;
      if (Math.abs(delta) < 1) { done?.(); return; }
      const started = performance.now();
      track.style.scrollSnapType = 'none';
      const frame = (now) => {
        if (destroyed) return;
        const p = clamp((now - started) / duration, 0, 1);
        track.scrollLeft = start + delta * ease(p);
        if (p < 1) scrollAnim = requestAnimationFrame(frame);
        else {
          scrollAnim = null;
          track.style.scrollSnapType = '';
          done?.();
        }
      };
      scrollAnim = requestAnimationFrame(frame);
    };

    const normalizePhysical = () => {
      if (count <= 1) { physicalIndex = 0; logicalIndex = 0; return; }
      if (physicalIndex < count) {
        physicalIndex += count;
        setScrollInstant(leftForPhysical(physicalIndex));
      } else if (physicalIndex >= count * 2) {
        physicalIndex -= count;
        setScrollInstant(leftForPhysical(physicalIndex));
      }
      logicalIndex = mod(physicalIndex - count, count);
    };

    const updateUI = () => {
      logicalIndex = mod(logicalIndex, count);
      originals.forEach((item, i) => item.classList.toggle('is-zpc7-active', i === logicalIndex));
      if (currentEl) currentEl.textContent = pad(logicalIndex + 1);
      if (totalEl) totalEl.textContent = pad(count);
      if (dotsWrap) [...dotsWrap.children].forEach((dot, i) => {
        const active = i === logicalIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const stopProgress = () => {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.width = '0%';
    };
    const startProgress = (delay = speed) => {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.width = '0%';
      progress.offsetWidth;
      progress.style.transition = `width ${delay}ms linear`;
      progress.style.width = '100%';
    };
    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      stopProgress();
    };
    const canAutoplay = () => {
      if (!autoplayEnabled || count <= 1 || destroyed || document.hidden || mqReduce.matches) return false;
      if (mqMobile.matches && !autoplayMobile) return false;
      if (designMode && !autoplayEditor) return false;
      return !(hovering || focusWithin || userPaused);
    };
    const schedule = (delay = speed) => {
      clearTimer();
      if (!canAutoplay()) return;
      startProgress(delay);
      timer = setTimeout(() => {
        timer = null;
        moveBy(direction, false);
      }, delay);
    };
    const pauseForInteraction = () => {
      clearTimer();
      if (!pauseInteraction) return;
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => schedule(), resumeDelay);
    };

    // Always exactly one product per movement.
    const moveBy = (delta, user = false) => {
      if (count <= 1) return;
      physicalIndex += delta > 0 ? 1 : -1;
      logicalIndex = mod(physicalIndex - count, count);
      updateUI();
      animateTo(leftForPhysical(physicalIndex), () => {
        normalizePhysical();
        updateUI();
        if (user) pauseForInteraction(); else schedule();
      });
    };

    const goLogical = (index, user = false) => {
      logicalIndex = mod(index, count);
      physicalIndex = count + logicalIndex;
      updateUI();
      animateTo(leftForPhysical(physicalIndex), () => {
        if (user) pauseForInteraction(); else schedule();
      });
    };

    const syncFromNativeScroll = () => {
      const step = Math.max(getStep(), 1);
      physicalIndex = Math.round(track.scrollLeft / step);
      logicalIndex = mod(physicalIndex - count, count);
      updateUI();
    };

    const settleNativeScroll = () => {
      syncFromNativeScroll();
      animateTo(leftForPhysical(physicalIndex), () => {
        normalizePhysical();
        updateUI();
      });
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'zpc7-dot';
        dot.setAttribute('aria-label', `Produkt ${i + 1}`);
        dot.addEventListener('click', () => goLogical(i, true));
        dotsWrap.appendChild(dot);
      }
      updateUI();
    };

    prev?.addEventListener('click', () => moveBy(-1, true));
    next?.addEventListener('click', () => moveBy(1, true));

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); moveBy(1, true); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); moveBy(-1, true); }
      if (event.key === 'Home') { event.preventDefault(); goLogical(0, true); }
      if (event.key === 'End') { event.preventDefault(); goLogical(count - 1, true); }
    });

    track.addEventListener('scroll', () => {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(syncFromNativeScroll);
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(settleNativeScroll, 120);
    }, { passive: true });

    const onTouchStart = () => { clearTimer(); stopScrollAnimation(); };
    const onTouchEnd = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        settleNativeScroll();
        pauseForInteraction();
      }, 90);
    };
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchend', onTouchEnd, { passive: true });
    track.addEventListener('touchcancel', onTouchEnd, { passive: true });

    if (pauseHover) {
      root.addEventListener('mouseenter', () => { hovering = true; clearTimer(); });
      root.addEventListener('mouseleave', () => { hovering = false; schedule(); });
      root.addEventListener('focusin', () => { focusWithin = true; clearTimer(); });
      root.addEventListener('focusout', () => { focusWithin = false; schedule(); });
    }

    if (dragEnabled && mqFine.matches) {
      track.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft };
        track.setPointerCapture?.(event.pointerId);
        track.classList.add('is-dragging');
        clearTimer();
        stopScrollAnimation();
      });
      track.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        if (Math.abs(dx) > 5) suppressClick = true;
        track.scrollLeft = drag.left - dx;
      });
      const finishDrag = (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        track.classList.remove('is-dragging');
        drag = null;
        settleNativeScroll();
        pauseForInteraction();
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
    }

    // Card entrance and modern desktop pointer effects.
    if (root.dataset.entryEnabled === 'true' && !mqReduce.matches) {
      root.classList.remove('is-zpc7-mounted', 'is-zpc7-entry-done');
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-zpc7-mounted')));
      const entryDuration = Math.max(0, parseFloat(getComputedStyle(root).getPropertyValue('--zpc7-entry-duration')) || 700);
      const entryStagger = Math.max(0, parseFloat(getComputedStyle(root).getPropertyValue('--zpc7-entry-stagger')) || 70);
      const entryTimer = setTimeout(() => root.classList.add('is-zpc7-entry-done'), entryDuration + Math.min(count, 10) * entryStagger + 100);
      cleanups.push(() => clearTimeout(entryTimer));
    } else root.classList.add('is-zpc7-mounted', 'is-zpc7-entry-done');

    if (!mqReduce.matches && mqFine.matches && ['tilt', 'spotlight'].includes(root.dataset.cardHover)) {
      allItems().forEach((card) => {
        const onMove = (event) => {
          const rect = card.getBoundingClientRect();
          const px = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
          const py = clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1);
          card.style.setProperty('--zpc7-spot-x', `${(px * 100).toFixed(1)}%`);
          card.style.setProperty('--zpc7-spot-y', `${(py * 100).toFixed(1)}%`);
          if (root.dataset.cardHover === 'tilt') {
            card.style.setProperty('--zpc7-tilt-x', `${((px - .5) * 7).toFixed(2)}deg`);
            card.style.setProperty('--zpc7-tilt-y', `${((.5 - py) * 6).toFixed(2)}deg`);
          }
        };
        const onLeave = () => {
          ['--zpc7-tilt-x','--zpc7-tilt-y','--zpc7-spot-x','--zpc7-spot-y'].forEach((name) => card.style.removeProperty(name));
        };
        card.addEventListener('pointermove', onMove, { passive: true });
        card.addEventListener('pointerleave', onLeave, { passive: true });
        cleanups.push(() => { card.removeEventListener('pointermove', onMove); card.removeEventListener('pointerleave', onLeave); });
      });
    }

    const recenter = () => {
      physicalIndex = count + logicalIndex;
      setScrollInstant(leftForPhysical(physicalIndex));
      updateUI();
    };
    const onVisibility = () => document.hidden ? clearTimer() : schedule();
    document.addEventListener('visibilitychange', onVisibility);
    mqReduce.addEventListener?.('change', recenter);
    mqMobile.addEventListener?.('change', recenter);
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(recenter);
      resizeObserver.observe(track);
    } else window.addEventListener('resize', recenter, { passive: true });

    root._zpc7GoLogical = goLogical;
    root._zpc7Destroy = () => {
      destroyed = true;
      clearTimer();
      clearTimeout(resumeTimer);
      clearTimeout(scrollEndTimer);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      stopScrollAnimation();
      resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      mqReduce.removeEventListener?.('change', recenter);
      mqMobile.removeEventListener?.('change', recenter);
      if (!resizeObserver) window.removeEventListener('resize', recenter);
      cleanups.forEach((fn) => fn());
      beforeClones.forEach((clone) => clone.remove());
      afterClones.forEach((clone) => clone.remove());
      delete root._zpc7GoLogical;
      delete root._zpc7Destroy;
      delete root.dataset.zpc7Ready;
    };

    buildDots();
    requestAnimationFrame(() => requestAnimationFrame(recenter));
    schedule();
  }

  function init(scope = document) {
    if (scope.matches?.(SELECTOR)) initCarousel(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initCarousel);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    root?._zpc7Destroy?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest?.('[data-zpc7-item]');
    const root = card?.closest?.(SELECTOR);
    if (!card || !root || card.dataset.zpc7Clone) return;
    const originals = [...root.querySelectorAll('[data-zpc7-item]:not([data-zpc7-clone])')];
    const index = originals.indexOf(card);
    if (index >= 0) root._zpc7GoLogical?.(index, true);
  });
})();
